/**
 * content.ts — Static content loader.
 *
 * Strategy: Vite's `import.meta.glob` is used to eagerly bundle MDX modules
 * and their frontmatter. The MDX rollup plugin extracts frontmatter into the
 * module's `frontmatter` named export.
 *
 * Project content lives at `content/projects/<slug>/index.mdx`.
 * Journal content lives at `content/journal/<slug>.mdx`.
 * Index files (`_index.json`) define display order + typology filters.
 */

import type { Project, JournalPost, ContactInfo, ProjectFrontmatter, JournalFrontmatter } from '@/types/content';

interface MdxModule {
  default: React.ComponentType;
  frontmatter?: Record<string, unknown>;
}

// Eagerly bundle all MDX modules at build time. Vite tree-shakes unused routes.
const projectModules = import.meta.glob<MdxModule>('../../content/projects/*/index.mdx', {
  eager: true,
});

const journalModules = import.meta.glob<MdxModule>('../../content/journal/*.mdx', {
  eager: true,
});

interface IndexFile {
  order: string[];
}

import projectsIndex from '../../content/projects/_index.json';
import journalIndex from '../../content/journal/_index.json';
import contactJson from '../../content/contact.json';

function projectSlugFromPath(path: string): string {
  const m = path.match(/\/projects\/([^/]+)\/index\.mdx$/);
  return m?.[1] ?? '';
}

function journalSlugFromPath(path: string): string {
  const m = path.match(/\/journal\/([^/]+)\.mdx$/);
  return m?.[1] ?? '';
}

export function getAllProjects(): Project[] {
  const order = (projectsIndex as IndexFile).order;
  const bySlug = new Map<string, Project>();

  for (const [path, mod] of Object.entries(projectModules)) {
    const slug = projectSlugFromPath(path);
    if (!slug) continue;
    const fm = (mod.frontmatter ?? {}) as Partial<ProjectFrontmatter>;
    bySlug.set(slug, {
      slug,
      title: fm.title ?? slug,
      year: fm.year ?? new Date().getFullYear(),
      typology: fm.typology ?? 'residential',
      location: fm.location ?? '',
      summary: fm.summary ?? '',
      heroImage: fm.heroImage ?? '',
      heroAlt: fm.heroAlt ?? fm.title ?? slug,
      materials: fm.materials,
      placeholder: fm.placeholder ?? false,
      gallery: fm.gallery,
      Body: mod.default,
    });
  }

  return order.map((slug) => bySlug.get(slug)).filter((p): p is Project => Boolean(p));
}

export function getProject(slug: string): Project | null {
  return getAllProjects().find((p) => p.slug === slug) ?? null;
}

export function getAllJournalPosts(): JournalPost[] {
  const order = (journalIndex as IndexFile).order;
  const bySlug = new Map<string, JournalPost>();

  for (const [path, mod] of Object.entries(journalModules)) {
    const slug = journalSlugFromPath(path);
    if (!slug) continue;
    const fm = (mod.frontmatter ?? {}) as Partial<JournalFrontmatter>;
    bySlug.set(slug, {
      slug,
      title: fm.title ?? slug,
      date: fm.date ?? new Date().toISOString(),
      excerpt: fm.excerpt ?? '',
      author: fm.author ?? 'Brian Ellison',
      category: fm.category,
      placeholder: fm.placeholder ?? false,
      Body: mod.default,
    });
  }

  return order
    .map((slug) => bySlug.get(slug))
    .filter((p): p is JournalPost => Boolean(p));
}

export function getJournalPost(slug: string): JournalPost | null {
  return getAllJournalPosts().find((p) => p.slug === slug) ?? null;
}

export function getContact(): ContactInfo {
  return contactJson as ContactInfo;
}
